/**
 * Unified Serverless Function: create-portal-session.js
 * 
 * Securely communicates with Stripe to:
 * 1. GET: Resolve a customer's active subscription status, plan name, price, and next billing date.
 * 2. POST: Create a Stripe Customer Billing Portal redirect session.
 * 
 * Deployment:
 * Host this on Vercel, Netlify, or AWS Lambda.
 * Set the Environment Variable: STRIPE_SECRET_KEY
 */

const stripe = require('stripe')(process.env.STRIPE_SECRET_KEY);

// Helper function to format timestamp to human-readable date
function formatDate(timestamp) {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}

module.exports = async (req, res) => {
  // 1. Enable CORS for Shopify browser requests
  res.setHeader('Access-Control-Allow-Origin', '*');
  res.setHeader('Access-Control-Allow-Methods', 'GET, POST, OPTIONS');
  res.setHeader('Access-Control-Allow-Headers', 'Content-Type, Authorization');

  // Handle preflight OPTIONS requests
  if (req.method === 'OPTIONS') {
    res.status(200).end();
    return;
  }

  // Retrieve email from query string (GET) or request body (POST)
  const email = (req.method === 'GET' ? req.query.email : req.body.email);

  if (!email || !email.includes('@')) {
    res.status(400).json({ message: 'A valid email address parameter is required.' });
    return;
  }

  try {
    // 2. Query Stripe for the customer matching this email
    console.log(`Searching Stripe for customer email: ${email}`);
    const customers = await stripe.customers.list({
      email: email,
      limit: 1
    });

    // If customer doesn't exist, return inactive status
    if (customers.data.length === 0) {
      console.log(`No customer record found for: ${email}`);
      res.status(200).json({
        status: 'inactive',
        active: false,
        message: 'No Stripe customer record found.'
      });
      return;
    }

    const stripeCustomer = customers.data[0];

    // ==========================================
    // ROUTE A: GET REQUEST (Fetch Subscription Status)
    // ==========================================
    if (req.method === 'GET') {
      console.log(`Fetching active subscriptions for customer: ${stripeCustomer.id}`);
      
      const subscriptions = await stripe.subscriptions.list({
        customer: stripeCustomer.id,
        status: 'active', // fetch active subscriptions
        limit: 1
      });

      // If no active subscriptions, check for trialing/past_due
      let sub = subscriptions.data[0];
      if (!sub) {
        const checkAll = await stripe.subscriptions.list({
          customer: stripeCustomer.id,
          limit: 1
        });
        sub = checkAll.data[0];
      }

      // Check subscription states
      const isActive = sub && (sub.status === 'active' || sub.status === 'trialing');

      if (isActive) {
        // Retrieve plan details
        const plan = sub.items.data[0].plan;
        const planName = plan.product ? (await stripe.products.retrieve(plan.product)).name : 'Desktop Premium License';
        
        // Format price (e.g. $19.99 / month)
        const amount = (plan.amount / 100).toFixed(2);
        const currency = plan.currency.toUpperCase();
        const interval = plan.interval;
        const priceText = `$${amount} ${currency} / ${interval}`;

        res.status(200).json({
          status: 'active',
          active: true,
          planName: planName,
          price: priceText,
          renewsAt: formatDate(sub.current_period_end)
        });
      } else {
        res.status(200).json({
          status: 'inactive',
          active: false,
          message: sub ? `Subscription is currently ${sub.status}` : 'No subscription active.'
        });
      }
      return;
    }

    // ==========================================
    // ROUTE B: POST REQUEST (Generate Billing Portal Link)
    // ==========================================
    if (req.method === 'POST') {
      const refererUrl = req.headers.referer || 'https://' + req.headers.host;
      
      console.log(`Generating billing portal session for customer: ${stripeCustomer.id}`);
      const session = await stripe.billingPortal.sessions.create({
        customer: stripeCustomer.id,
        return_url: refererUrl,
      });

      res.status(200).json({
        url: session.url
      });
      return;
    }

    // fallback
    res.status(405).json({ message: 'Method Not Allowed' });

  } catch (error) {
    console.error(`API Error for ${email}:`, error);
    res.status(500).json({
      message: 'Failed to communicate securely with Stripe.',
      error: error.message
    });
  }
};
